import { ButtonBase, Stack, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { Button, Card, HSpacer, Text } from '@/components/DesignSystem';
import { ApiCategoryMapping } from '@api/interfaces/ApiCategoryMapping';
import {
  getProgramPeriodText,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/RewardsPrograms/helpers';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { getProgramStatus } from '@shared/utilities';
import { ProgramStatus } from '@shared/enums/ProgramStatus';
import ContentCopy from '@mui/icons-material/ContentCopy';

interface CategoryMappingTableProps {
  isJobProcessing?: boolean,
  onDuplicate: (id: string) => void,
  onEdit: (id: string) => void,
  onView: (id: string) => void,
  mappings: ApiCategoryMapping[],
  programs?: ApiRewardsProgram[],
}

export const CategoryMappingTable = ({
  isJobProcessing = false,
  mappings,
  onDuplicate,
  onEdit,
  onView,
  programs,
}: CategoryMappingTableProps) => {
  const theme = useTheme();

  return (
    <Stack spacing={1} width="100%">
      {mappings
        .sort((a, b) => (
          b.createdAt.getTime() - a.createdAt.getTime()
        ))
        .map((mapping) => {
          const createdDateText = DateTime.fromJSDate(mapping.createdAt).toFormat('MMM d, yyyy');
          const mappedProgram = mapping.programId
            ? programs?.find((program) => (
              program.id === mapping.programId
            ))
            : null;
          const status = mappedProgram
            ? getProgramStatus(mappedProgram)
            : null;
          const canEdit = (
            status !== ProgramStatus.Expired
            && !(
              status === ProgramStatus.Active
              && isJobProcessing
            )
          );

          return (
            <Card
              cardContentStyle={{ '&:last-child': { paddingBottom: 0 }, padding: 0 }}
              elevation={0}
              key={mapping.id}
              testID={`category-mapping-card-${mapping.id}`}
            >
              <ButtonBase
                disableRipple
                onClick={() => onView(mapping.id)}
                sx={{ p: '16px 0px 16px 16px', height: '52px', width: '100%' }}
              >
                <Stack
                  alignItems="center"
                  direction="row"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Stack alignItems="center" direction="row">
                    <Stack alignItems="center" direction="row" width="406px">
                      <Text sx={{ color: theme.palette.text.secondary }}>
                        Name:
                      </Text>
                      <HSpacer size="2"/>
                      <Text>
                        {mapping.name}
                      </Text>
                    </Stack>
                    <HSpacer size="4"/>
                    <Stack alignItems="center" direction="row" width="180px">
                      <Text sx={{ color: theme.palette.text.secondary }}>
                        Created:
                      </Text>
                      <HSpacer size="2"/>
                      <Text>
                        {createdDateText}
                      </Text>
                    </Stack>
                    {!!mappedProgram && (
                      <>
                        <HSpacer size="4"/>
                        <Stack alignItems="center" direction="row" width="280px">
                          <Text sx={{ color: theme.palette.text.secondary }}>
                            Program period:
                          </Text>
                          <HSpacer size="2"/>
                          <Text>
                            {getProgramPeriodText(mappedProgram)}
                          </Text>
                        </Stack>
                      </>
                    )}
                  </Stack>
                  <Stack direction="row">
                    {canEdit && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(mapping.id);
                        }}
                        startIcon={<EditOutlined />}
                        testID={`category-mapping-edit-button-${mapping.id}`}
                        variant="text"
                      >
                        Edit
                      </Button>
                    )}
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate(mapping.id);
                      }}
                      startIcon={<ContentCopy/>}
                      testID={`category-mapping-duplicate-button-${mapping.id}`}
                      variant="text"
                    >
                      Duplicate
                    </Button>
                  </Stack>
                </Stack>
              </ButtonBase>
            </Card>
          );
        })}
    </Stack>
  );
};